/**
 * «Lo que el plano no traía y se dedujo»: UN lugar por plano donde la persona ve qué datos NO venían en el
 * archivo y el sistema dedujo, cada uno con de dónde salió. Pedido de Sebastián (2026-10-01): «el usuario
 * debe ver de alguna manera que no existían en el plano ciertos datos y que los infirió».
 *
 * Solo lista lo que ya está calculado y guardado (no pide nada nuevo ni inventa): materiales reconocidos
 * por su nombre (IFC), niveles deducidos de las losas (IFC sin pisos), altura de muros propuesta (DWG),
 * rol sugerido del plano y capas clasificadas (DWG), incluidas las que tienen forma de muro y no lo son
 * (unos ejes de retícula: líneas largas y paralelas) con el porqué de la IA. Si no hay nada, no se muestra.
 */
import { Popover, PopoverSurface, PopoverTrigger, makeStyles } from '@fluentui/react-components';
import {
  MENSAJE_IA_NO_RESPONDE,
  NOMBRE_DE_TIPO_DE_MATERIAL,
  etiquetaRolArchivo,
  familiaDeTipoDeMaterial,
  type CapaConFormaDeMuro,
  type CapasClasificadasDTO,
  type DecisionDeCapa,
  type MotivoFalloIa,
  type MaterialDeducidoIfc,
  type PlanoAlturaPropuesta,
  type PlanoRolArchivo,
} from '@supernova/shared';

const useStyles = makeStyles({
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '24px',
    padding: '0 10px',
    borderRadius: '12px',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-surface)',
    color: 'var(--text-secondary)',
    fontSize: '12px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    ':hover': { background: 'var(--bg-hover)', color: 'var(--text-primary)' },
  },
  conAviso: { borderTopColor: 'var(--warn)', borderRightColor: 'var(--warn)', borderBottomColor: 'var(--warn)', borderLeftColor: 'var(--warn)' },
  surface: { width: '400px', maxWidth: 'calc(100vw - 24px)', padding: '14px 16px 12px' },
  titulo: { margin: '0 0 4px', fontSize: '14.5px', lineHeight: 1.35, fontWeight: 600 },
  intro: { margin: '0 0 10px', fontSize: '12.5px', color: 'var(--text-tertiary)', lineHeight: 1.45 },
  aviso: { margin: '0 0 10px', fontSize: '12.5px', color: 'var(--warn)', lineHeight: 1.45 },
  lista: { listStyleType: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '10px' },
  item: { display: 'flex', flexDirection: 'column', gap: '2px' },
  que: { fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.4 },
  deDonde: { fontSize: '12px', color: 'var(--text-tertiary)', lineHeight: 1.4 },
  verDonde: {
    marginLeft: '6px',
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: 'var(--accent)',
    fontSize: '12.5px',
    cursor: 'pointer',
    textDecorationLine: 'underline',
  },
  sub: { margin: '2px 0 0', paddingLeft: '14px', fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.5 },
});

export interface LoQueSeDedujoProps {
  materiales?: MaterialDeducidoIfc[] | null;
  /** IA caída al reconocer materiales: el texto único. */
  avisoMateriales?: string | null;
  /** Material (nombre) que está resaltado en el visor 3D ahora, o null. */
  materialResaltado?: string | null;
  /** «Ver dónde»: resalta en el visor las piezas de ese material; null = «Quitar resaltado». Sin esto no hay botón. */
  onVerMaterial?: (m: MaterialDeducidoIfc | null) => void;
  nivelesDeducidos?: number;
  altura?: PlanoAlturaPropuesta | null;
  rolSugerido?: { rol: PlanoRolArchivo; razon: string } | null;
  capasClasificadas?: CapasClasificadasDTO | null;
  /** DWG: capas que el lector midió como muro y la IA reconoció como otra cosa (`capasQueParecenMuro`). */
  conFormaDeMuro?: CapaConFormaDeMuro[];
  /** Correcciones de la persona con el ⋯ (capa o bloque → decisión), guardadas por plano. */
  corregidas?: Readonly<Record<string, DecisionDeCapa>>;
  /** DWG: la IA no pudo decir qué es cada capa (las capas se miden solo por su forma). */
  falloCapas?: MotivoFalloIa | null;
}

const fmtM = (m: number) => m.toLocaleString('es-MX', { maximumFractionDigits: 2 });

const piezas = (n: number) => (n === 1 ? '1 pieza' : `${n.toLocaleString('es-MX')} piezas`);

export function LoQueSeDedujo(p: LoQueSeDedujoProps) {
  const s = useStyles();
  const items: { k: string; que: React.ReactNode; deDonde: string }[] = [];

  const mats = p.materiales ?? [];
  if (mats.length) {
    items.push({
      k: 'materiales',
      que: (
        <>
          Material de las piezas
          <ul className={s.sub}>
            {mats.map((m) => (
              <li key={m.nombre}>
                «{m.nombre}» → {NOMBRE_DE_TIPO_DE_MATERIAL[m.tipo]}
                {' · '}
                {familiaDeTipoDeMaterial(m.tipo)
                  ? m.pesadas > 0
                    ? `${piezas(m.pesadas)} ${m.pesadas === 1 ? 'pesada' : 'pesadas'} así`
                    : `${piezas(m.piezas)}, sin volumen para pesar`
                  : `${piezas(m.piezas)} sin pesar`}
                {p.onVerMaterial && m.expressIds && m.expressIds.length > 0 && (
                  <button
                    type="button"
                    className={s.verDonde}
                    title={
                      p.materialResaltado === m.nombre
                        ? 'Vuelve a mostrar el modelo con sus colores.'
                        : 'Ilumina en el modelo 3D las piezas que tienen este material.'
                    }
                    onClick={() => p.onVerMaterial?.(p.materialResaltado === m.nombre ? null : m)}
                  >
                    {p.materialResaltado === m.nombre ? 'Quitar resaltado' : 'Ver dónde'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      ),
      deDonde:
        'El modelo no dice de qué material son. La IA lo reconoció por el nombre del material; el peso de esas piezas está marcado como estimado.',
    });
  }
  if (p.nivelesDeducidos) {
    items.push({
      k: 'niveles',
      que: p.nivelesDeducidos === 1 ? '1 nivel' : `${p.nivelesDeducidos} niveles`,
      deDonde: 'El modelo no trae pisos: se sacaron de la altura de sus losas de piso.',
    });
  }
  if (p.altura?.alturaM != null) {
    items.push({
      k: 'altura',
      que: `Altura de los muros: ${p.altura.alturaM.toLocaleString('es-MX')} m`,
      deDonde: p.altura.razon || 'La IA la leyó de las anotaciones del plano.',
    });
  }
  if (p.rolSugerido) {
    items.push({
      k: 'rol',
      que: `Rol sugerido: ${etiquetaRolArchivo(p.rolSugerido.rol)}`,
      deDonde: p.rolSugerido.razon || 'Se sugirió por el contenido del plano.',
    });
  }
  const cc = p.capasClasificadas;
  if (cc) {
    const partes = [
      [cc.wall.length, 'de muros'],
      [cc.door.length, 'de puertas'],
      [cc.window.length, 'de ventanas'],
      [cc.installation.length, 'de instalaciones'],
      [cc.budgetable?.length ?? 0, 'de otra obra'],
      [cc.noise?.length ?? 0, 'que no se cotizan'],
    ]
      .filter(([n]) => (n as number) > 0)
      .map(([n, t]) => `${n} ${t}`);
    if (partes.length) {
      items.push({
        k: 'capas',
        que: `Qué es cada capa: ${partes.join(', ')}`,
        deDonde: 'El dibujo no dice qué es cada capa: la IA las reconoció por su nombre y lo que contienen.',
      });
    }
  }
  const forma = p.conFormaDeMuro ?? [];
  if (forma.length) {
    items.push({
      k: 'forma-de-muro',
      que: (
        <>
          {forma.length === 1 ? 'Esta capa parece muro y no se mide como muro' : 'Estas capas parecen muros y no se miden como muros'}
          <ul className={s.sub}>
            {forma.map((c) => (
              <li key={c.capa}>
                «{c.capa}»{c.metros != null ? ` · ${fmtM(c.metros)} m` : ''}
                {' → '}
                {c.corregida
                  ? `Corregido por ti: ${c.decision === 'ruido' ? 'es solo dibujo' : 'se construye'}`
                  : c.decision === 'ruido'
                  ? c.motivo
                    ? `no se cotiza: ${c.motivo}`
                    : 'no se cotiza: es una ayuda de dibujo'
                  : c.decision === 'dudoso'
                    ? 'por decidir si se construye'
                    : 'se cotiza como otra obra, no como muro'}
              </li>
            ))}
          </ul>
        </>
      ),
      deDonde:
        'Por su forma (líneas largas y paralelas) parecen muros, pero la IA reconoció qué son. Si alguna sí es muro, muévela con el ⋯ de la capa a «Se construye».',
    });
  }

  // Lo que la persona corrigió con el ⋯ (las de forma de muro ya salen arriba, con su corrección).
  const enForma = new Set(forma.map((c) => c.capa));
  const corregidas = Object.entries(p.corregidas ?? {}).filter(([k, d]) => !enForma.has(k) && d !== 'dudoso');
  if (corregidas.length) {
    items.push({
      k: 'corregidas',
      que: (
        <>
          {corregidas.length === 1 ? 'Corregiste qué es esta capa' : 'Corregiste qué son estas capas'}
          <ul className={s.sub}>
            {corregidas.map(([k, d]) => (
              <li key={k}>
                «{k}» → Corregido por ti: {d === 'ruido' ? 'es solo dibujo' : 'se construye'}
              </li>
            ))}
          </ul>
        </>
      ),
      deDonde: 'Tu corrección manda sobre lo que dedujo la IA y queda guardada en este plano.',
    });
  }
  const avisoCapas = p.falloCapas
    ? `No se pudo reconocer qué es cada capa, así que se miden solo por su forma: revisa que unos ejes o cotas no entren como muros. ${
        p.falloCapas === 'ai_unavailable' ? MENSAJE_IA_NO_RESPONDE : 'Vuelve a leer el plano para intentarlo de nuevo.'
      }`
    : null;
  const conAviso = !!(p.avisoMateriales || avisoCapas);
  if (!items.length && !conAviso) return null;
  return (
    <Popover withArrow positioning={{ position: 'below', align: 'center' }}>
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className={conAviso ? `${s.trigger} ${s.conAviso}` : s.trigger}
          aria-label="Lo que el plano no traía y se dedujo"
          title="Lo que el plano no traía y se dedujo"
        >
          Lo que se dedujo{items.length ? ` (${items.length})` : ''}
        </button>
      </PopoverTrigger>
      <PopoverSurface className={s.surface} aria-label="Lo que el plano no traía y se dedujo">
        <h3 className={s.titulo}>Lo que el plano no traía y se dedujo</h3>
        <p className={s.intro}>Estos datos no venían en el archivo. Revísalos antes de usarlos en el presupuesto.</p>
        {p.avisoMateriales && (
          <p className={s.aviso}>
            No se pudo reconocer el material de algunas piezas, así que quedan sin pesar. {p.avisoMateriales}
          </p>
        )}
        {avisoCapas && <p className={s.aviso}>{avisoCapas}</p>}
        <ul className={s.lista}>
          {items.map((it) => (
            <li key={it.k} className={s.item}>
              <span className={s.que}>{it.que}</span>
              <span className={s.deDonde}>{it.deDonde}</span>
            </li>
          ))}
        </ul>
      </PopoverSurface>
    </Popover>
  );
}
